import { useEffect, useMemo, useState } from 'react';

import { SORT_DIRECTIONS, SortConfig } from '../../../../../../utils/constants/commonConstants';
import {
    ACCOUNT_STATUS_FIELD,
    columnWidthFor,
    CreditSummaryHeader,
    CreditSummaryRow,
    stickyLeftOffset,
    tableWidthFor,
    ACTIVE_ACCOUNT_STATUS,
    AMOUNT_TOTAL_FIELDS,
    CREDIT_SUMMARY_COLUMNS,
    compareCellValues,
    CreditSummaryValueType,
    formatAmountTotal,
    isMissingCellValue,
    parseAmountCell,
    readComparableCellValue,
} from './CreditSummaryTable.constants';

/**
 * Filter + sort derivation for the Credit Summary table.
 *
 * Extracted from the component and exported as a pure function so the value comparisons — which is
 * where this feature can silently go wrong — are unit-testable without React. Both operations are
 * client-side: polus returns the entire credit file in one unpaginated call, so every row is already
 * in memory and neither narrowing nor reordering needs a request.
 */

/** Range/date-range bounds as the shared filter renderers emit them (epoch ms for dates). */
type RangeBound = { gte?: number | null; lte?: number | null };

/** No constraint: absent, cleared to null, or an empty array (how the renderers clear themselves). */
export function isEmptyFilterValue(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') {
        const bound = (value.value || {}) as RangeBound;
        return (bound.gte === null || bound.gte === undefined) && (bound.lte === null || bound.lte === undefined);
    }
    return false;
}

function matchesMultiSelect(row: any, field: string, selected: any[]): boolean {
    const raw = row?.[field];
    if (isMissingCellValue(raw)) return false;
    return selected.map(String).includes(String(raw).trim());
}

/**
 * A row satisfies a bound only if it has a comparable value. A `-` current overdue means "not
 * reported", not "₹0" — admitting it into "at least ₹30 lakh" (or worse, treating it as 0 and
 * admitting it into "at most ₹1 lakh") would report absence as a real figure.
 */
function matchesRange(row: any, field: string, bound: RangeBound): boolean {
    const value = readComparableCellValue(row, field);
    if (value === null) return false;
    if (bound.gte !== null && bound.gte !== undefined && value < bound.gte) return false;
    if (bound.lte !== null && bound.lte !== undefined && value > bound.lte) return false;
    return true;
}

function applyFilters(rows: any[], appliedFilters: Record<string, any> | undefined): any[] {
    const active = Object.entries(appliedFilters || {}).filter(([, value]) => !isEmptyFilterValue(value));
    if (!active.length) return rows;

    // Every active filter must hold — a lender AND a date range AND an amount range narrow together.
    return rows.filter((row) =>
        active.every(([field, value]) => {
            const behaviour = CREDIT_SUMMARY_COLUMNS[field];
            if (!behaviour) return true; // filter for a column this table no longer has — ignore it
            if (Array.isArray(value)) return matchesMultiSelect(row, field, value);
            return matchesRange(row, field, (value?.value || {}) as RangeBound);
        }),
    );
}

/**
 * Single active sort column. Rows with no comparable value go last in BOTH directions — they are
 * "not reported", so they are never the smallest nor the largest.
 * Array.prototype.sort is stable, so tied rows keep polus's ordering (director, then active before
 * closed, then newest sanction first).
 */
function applySort(rows: any[], sortConfig: SortConfig | undefined): any[] {
    if (!sortConfig?.direction || !sortConfig?.field) return rows;

    const behaviour = CREDIT_SUMMARY_COLUMNS[sortConfig.field];
    if (!behaviour?.sortable) return rows;

    const ascending = sortConfig.direction === SORT_DIRECTIONS.ASC;

    return [...rows].sort((rowA, rowB) => {
        const a = readComparableCellValue(rowA, sortConfig.field);
        const b = readComparableCellValue(rowB, sortConfig.field);

        if (a === null && b === null) return 0;
        if (a === null) return 1;
        if (b === null) return -1;

        const order = compareCellValues(a, b);
        return ascending ? order : -order;
    });
}

export const UNKNOWN_ACCOUNT_STATUS = 'Unknown';

export type CreditSummaryGroup = {
    status: string;
    rows: any[];
    totals: Record<string, string>;
};

function totalsForGroup(rows: any[]): Record<string, string> {
    const totals: Record<string, string> = {};

    AMOUNT_TOTAL_FIELDS.forEach((field) => {
        let sum = 0;
        let anyReported = false;

        rows.forEach((row) => {
            const value = parseAmountCell(row?.[field]);
            if (value === null) return;
            sum += value;
            anyReported = true;
        });

        if (anyReported) totals[field] = formatAmountTotal(sum);
    });

    return totals;
}

export function groupRowsByStatus(rows: any[]): CreditSummaryGroup[] {
    const order: string[] = [];
    const buckets = new Map<string, any[]>();

    (rows || []).forEach((row) => {
        const raw = row?.[ACCOUNT_STATUS_FIELD];
        const status = isMissingCellValue(raw) ? UNKNOWN_ACCOUNT_STATUS : String(raw).trim();
        if (!buckets.has(status)) {
            buckets.set(status, []);
            order.push(status);
        }
        (buckets.get(status) as any[]).push(row);
    });

    const ordered = [
        ...order.filter((status) => status === ACTIVE_ACCOUNT_STATUS),
        ...order.filter((status) => status !== ACTIVE_ACCOUNT_STATUS),
    ];

    return ordered.map((status) => {
        const groupRows = buckets.get(status) as any[];
        return { status, rows: groupRows, totals: totalsForGroup(groupRows) };
    });
}

/** How many leading columns stay pinned while the table scrolls sideways. */
export const FROZEN_COLUMN_COUNT = 2;

export type CreditSummaryColumnStyle = { width: string; left?: string };

export type CreditSummaryView = {
    /** Rows to render: filtered, then sorted. */
    displayRows: any[];
    groups: CreditSummaryGroup[];
    /** Rows before filtering — lets the caller tell "no data at all" from "filtered to nothing". */
    totalRows: number;
};

export type CreditSummaryGeometry = {
    /** Per-column width, plus the sticky offset for the frozen ones. */
    columnStyles: CreditSummaryColumnStyle[];
    /** Width declared on the table — the sum of the column widths above. */
    tableWidth: string;
};

/**
 * Column geometry for the table. Kept next to the row derivation because it reads the same
 * response, and because the widths must agree with each other: the table declares the sum of its
 * columns, so both come from one place rather than being recomputed at the call site.
 */
export function buildCreditSummaryGeometry(
    headers: CreditSummaryHeader[] | undefined,
    rows: CreditSummaryRow[] | undefined,
): CreditSummaryGeometry {
    const allHeaders = headers || [];
    const allRows = rows || [];

    return {
        columnStyles: allHeaders.map((header, index) => ({
            width: columnWidthFor(header, allRows),
            ...(index < FROZEN_COLUMN_COUNT && { left: stickyLeftOffset(allHeaders, index, allRows) }),
        })),
        tableWidth: tableWidthFor(allHeaders, allRows),
    };
}

/**
 * Column geometry, recomputed when the webfont finishes loading.
 *
 * The two frozen columns are sized from measured text. If the first measurement runs before Gilroy
 * is available, canvas measures a fallback face and the memo - keyed on headers and rows - would
 * hold those wrong widths for the rest of the session. `document.fonts.ready` gives us one
 * recompute at the point the real metrics exist.
 */
export function useCreditSummaryGeometry(
    headers: CreditSummaryHeader[] | undefined,
    rows: CreditSummaryRow[] | undefined,
): CreditSummaryGeometry {
    const [fontsReady, setFontsReady] = useState(false);

    useEffect(() => {
        if (typeof document === 'undefined' || !document.fonts) return;

        let current = true;
        document.fonts.ready.then(() => {
            if (current) setFontsReady(true);
        });

        return () => {
            current = false;
        };
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => buildCreditSummaryGeometry(headers, rows), [headers, rows, fontsReady]);
}

export function buildCreditSummaryView(
    rows: any[] | undefined,
    appliedFilters: Record<string, any> | undefined,
    sortConfig: SortConfig | undefined,
): CreditSummaryView {
    const allRows = rows || [];
    // Filter first, then sort — so switching a filter on never scrambles the chosen ordering, and
    // changing the sort never quietly widens the row set.
    const displayRows = applySort(applyFilters(allRows, appliedFilters), sortConfig);

    return {
        displayRows,
        groups: groupRowsByStatus(displayRows),
        totalRows: allRows.length,
    };
}

/** Memoised React wrapper around buildCreditSummaryView. */
export default function useCreditSummaryRows(
    rows: any[] | undefined,
    appliedFilters: Record<string, any> | undefined,
    sortConfig: SortConfig | undefined,
): CreditSummaryView {
    return useMemo(() => buildCreditSummaryView(rows, appliedFilters, sortConfig), [rows, appliedFilters, sortConfig]);
}

/**
 * Whether anything is actually narrowing the table — drives the header's active state and its clear
 * (✕) affordance.
 *
 * Shares its emptiness definition (isEmptyFilterValue) with applyFilters, so "shows as active" and
 * "actually filtering" can never disagree. Note values arriving through DataTableFilterDrawer are
 * always arrays or populated range objects — its handleFilterChange coalesces a renderer's cleared
 * `null` to `[]` before apply — so the null handling here is defensive, for writers that bypass the
 * drawer, not a behaviour difference from `utils#isAppliedFiltersEmpty` in the drawer flow.
 */
export function hasActiveCreditSummaryFilters(appliedFilters: Record<string, any> | undefined): boolean {
    return Object.values(appliedFilters || {}).some((value) => !isEmptyFilterValue(value));
}

/** none -> ASC -> DESC -> none on the same column; a different column starts at ASC. */
export function nextSortConfig(current: SortConfig | undefined, field: string): SortConfig | undefined {
    if (current?.field !== field) return { field, direction: SORT_DIRECTIONS.ASC };
    if (current.direction === SORT_DIRECTIONS.ASC) return { field, direction: SORT_DIRECTIONS.DESC };
    return undefined;
}
